import React from 'react';
import Tooltip from './Tooltip';

// Pill-shaped portal-visibility toggle, three states:
//
//   'on'     — Visible in Portal      (green tint, open-eye icon, switch right)
//   'off'    — Hidden                 (neutral, eye-off icon, switch left)
//   'locked' — By Risk Tier / Locked Off
//              (dim, padlock icon, switch left, click does nothing)
//
// Used in:
//   - FusionDocuments row visibility cell
//   - DocumentConfigModal "Show in supplier portal" toggle
//
// The two consumers stay in sync because state lives one level up — both
// just render this component with the current value and an onChange
// handler.
//
// Locked variant carries a `lockedReason` so the hover tooltip explains
// why the toggle can't be flipped. Two reasons today:
//
//   'by-risk-tier'     — Compliance docs: visibility is set by which risk
//                        tiers require this doc, in Risk Flag Config.
//   'category-locked'  — Internal Reference docs: never visible. Admin
//                        must change category first.
//
// onChange is only called when state is 'on' or 'off'; locked clicks are
// silent. Disabled is a separate flag for "API call in flight" — same
// dim treatment as locked but no tooltip override.

const ICON_PROPS = {
  width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none',
  stroke: 'currentColor', strokeWidth: 2,
  strokeLinecap: 'round', strokeLinejoin: 'round',
  'aria-hidden': true,
};

const EyeIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const LockIcon = () => (
  <svg {...ICON_PROPS}>
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const LABEL = {
  on: 'Visible in Portal',
  off: 'Hidden',
  'locked:by-risk-tier': 'By Risk Tier',
  'locked:category-locked': 'Locked Off',
  'locked:draft': 'Not Live Yet',
};

const LOCKED_TOOLTIP = {
  'by-risk-tier': 'Visibility is set by risk tier — edit in Risk Flag Config.',
  'category-locked': 'Internal Reference documents are admin-only. Change the category first to make this document visible.',
  'draft': "Drafts aren't shown in the portal — publish the document to make it visible.",
};

export default function PortalToggle({
  state,
  lockedReason = 'by-risk-tier',
  onChange,
  disabled = false,
}) {
  const isLocked = state === 'locked';
  const isOn = state === 'on';
  const labelKey = isLocked ? `locked:${lockedReason}` : state;
  const label = LABEL[labelKey] || LABEL.off;

  const handleClick = () => {
    if (isLocked || disabled) return;
    onChange?.(!isOn);
  };

  const bgClass = isLocked
    ? 'bg-white border-line'
    : isOn
      ? 'bg-[#92D400]/[0.06] border-[#92D400]/30'
      : 'bg-white border-line';

  const iconColor = isLocked
    ? 'text-[#51534A]'
    : isOn
      ? 'text-fusion-forest'
      : 'text-[#51534A]';

  const labelColor = isLocked
    ? 'text-[#51534A]'
    : isOn
      ? 'text-fusion-forest'
      : 'text-[#51534A]';

  // Mini switch — 28x16 pill, white dot slides left/right. Disabled +
  // locked variants share the same dim treatment.
  const switchBg = isOn ? 'bg-[#92D400]' : 'bg-[#51534A]';
  const switchOpacity = (isLocked || disabled) ? 'opacity-50' : '';

  const button = (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || isLocked}
      aria-pressed={isOn}
      aria-label={`Portal visibility: ${label}`}
      className={`inline-flex items-center justify-between gap-2 min-w-[154px] px-3 py-1.5 rounded-full border transition-colors ${bgClass} ${(isLocked || disabled) ? 'opacity-[0.45] cursor-not-allowed' : 'cursor-pointer hover:border-[#DADCCD]'}`}>
      <span className={`flex-shrink-0 ${iconColor}`}>
        {isLocked ? <LockIcon /> : isOn ? <EyeIcon /> : <EyeOffIcon />}
      </span>
      <span className={`font-['Metropolis'] text-[10px] font-bold tracking-[0.15em] uppercase ${labelColor} flex-1 text-center`}>
        {label}
      </span>
      <span className={`relative w-7 h-4 rounded-full flex-shrink-0 transition-colors ${switchBg} ${switchOpacity}`}>
        <span
          className="absolute top-[2px] w-3 h-3 rounded-full bg-[#F9FCF1] transition-transform"
          style={{
            left: 2,
            transform: isOn ? 'translateX(12px)' : 'translateX(0)',
          }}
          aria-hidden="true" />
      </span>
    </button>
  );

  if (isLocked) {
    return (
      <Tooltip content={LOCKED_TOOLTIP[lockedReason] || LOCKED_TOOLTIP['by-risk-tier']}>
        {button}
      </Tooltip>
    );
  }
  return button;
}
